<template>
  <AdminLayout>
  <div class="d-flex flex-column">
    <div class="d-flex flex-row align-center mb-3">
      <h1 class="outfit-regular">Users</h1>
      <v-spacer></v-spacer>
      <v-btn color="black" icon dark :loading="loading" @click="reload">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>
    <v-progress-linear :value="progressValue" stream color="yellow darken-3" class="mt-2 mb-6"></v-progress-linear>

    <v-alert v-if="errorMessage" type="error" dense class="mb-4" text>{{ errorMessage }}</v-alert>

    <div class="d-flex flex-row align-center mb-4">
      <v-text-field v-model="search" solo outlined dense prepend-inner-icon="mdi-magnify" label="Search users (username or email)" hide-details></v-text-field>
    </div>

    <v-card outlined>
      <v-simple-table>
        <thead>
          <tr>
            <th class="text-left" style="width:64px">Avatar</th>
            <th class="text-left">Username</th>
            <th class="text-left">Email</th>
            <th class="text-left" style="width:160px">Sparks</th>
            <th class="text-left" style="width:120px">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="u in filteredUsers" :key="u.id" @click="openUser(u)" style="cursor:pointer">
            <td class="py-2">
              <v-avatar size="40">
                <v-img :src="u.avatar || ''"></v-img>
              </v-avatar>
            </td>
            <td><strong>{{ u.username || '—' }}</strong></td>
            <td>{{ u.email }}</td>
            <td>
              <div class="d-flex flex-row align-center">
                <v-chip small color="yellow darken-3" class="black--text">{{ u.sparks || 0 }}</v-chip>
              </div>
            </td>
            <td>
              <v-btn small color="black" dark @click.stop="openUser(u)">Manage</v-btn>
            </td>
          </tr>
          <tr v-if="!loading && filteredUsers.length === 0">
            <td colspan="5" class="text-center grey--text">No users found.</td>
          </tr>
          <tr v-if="loading">
            <td colspan="5" class="text-center">
              <v-progress-circular indeterminate color="deep-purple"></v-progress-circular>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="720">
      <v-card>
        <v-card-title>Manage User</v-card-title>
        <v-card-text>
          <v-alert v-if="dialogError" type="error" dense text class="mb-4">{{ dialogError }}</v-alert>
          <div class="d-flex flex-row align-start">
            <div class="mr-4">
              <v-avatar size="96" @click="$refs.avatarInput.click()">
                <input ref="avatarInput" type="file" accept="image/*" @change="onAvatarSelected" style="display:none">
                <v-img :src="editUser.avatar || ''"></v-img>
              </v-avatar>
            </div>
            <div class="flex-1" style="min-width:0">
              <v-text-field v-model="editUser.username" label="Username" outlined hide-details class="mb-3"></v-text-field>
              <v-text-field v-model="editUser.email" label="Email" outlined hide-details disabled class="mb-3"></v-text-field>
              <div class="text-overline mb-1">Sparks</div>
              <div class="d-flex flex-row align-center">
                <v-btn icon @click="adjustSparks(-1)" :disabled="saving"><v-icon>mdi-minus</v-icon></v-btn>
                <v-text-field v-model.number="editUser.sparks" type="number" outlined hide-details style="max-width: 140px" class="mx-2"></v-text-field>
                <v-btn icon @click="adjustSparks(1)" :disabled="saving"><v-icon>mdi-plus</v-icon></v-btn>
              </div>
              <v-divider class="my-4"></v-divider>
              <div class="text-overline mb-2">Realm & Character</div>
              <v-select
                :items="realms"
                item-text="name"
                item-value="id"
                v-model="selectedRealmId"
                @change="onRealmChange"
                outlined
                dense
                hide-details
                label="Select Realm"
                :loading="loadingRealms"
                class="mb-3"
              ></v-select>
              <v-select
                :items="characters"
                item-text="name"
                item-value="id"
                v-model="selectedCharacterId"
                outlined
                dense
                hide-details
                label="Select Character"
                :loading="loadingCharacters"
                :disabled="!selectedRealmId"
              >
                <template v-slot:selection="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar v-if="item && item.avatar" size="20" class="mr-2">
                      <v-img :src="item.avatar"></v-img>
                    </v-avatar>
                    <span>{{ item && item.name ? item.name : '' }}</span>
                  </div>
                </template>
                <template v-slot:item="{ item }">
                  <div class="d-flex align-center py-1">
                    <v-avatar v-if="item && item.avatar" size="24" class="mr-2">
                      <v-img :src="item.avatar"></v-img>
                    </v-avatar>
                    <span>{{ item && item.name ? item.name : '' }}</span>
                  </div>
                </template>
              </v-select>
              <div class="d-flex flex-row align-center mt-2" v-if="selectedCharacter">
                <v-avatar size="128" tile class="mr-2"><v-img :src="selectedCharacter.avatar || ''"></v-img></v-avatar>
                <v-btn x-small class="ml-2" text @click="applySelectedCharacterToProfile" :disabled="!selectedCharacter">Apply to username/avatar</v-btn>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog=false" :disabled="saving">Cancel</v-btn>
          <v-btn color="black" dark :loading="saving" @click="save">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
  </AdminLayout>
</template>

<script>
import { collection, getDocs, doc, updateDoc, writeBatch, serverTimestamp, getDoc, setDoc } from 'firebase/firestore'
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db } from '@/firebase/init'
import AdminLayout from '@/layouts/Admin.vue'

export default {
  name: 'AdminUsers',
  components: { AdminLayout },
  data() {
    return {
      progressValue: 0,
      loading: false,
      users: [],
      search: '',
      errorMessage: '',
      dialog: false,
      editUser: { id: '', username: '', email: '', avatar: '', sparks: 0 },
      saving: false,
      dialogError: '',
      uploadingAvatar: false,
      storage: null,
      // Realms & characters
      realms: [],
      loadingRealms: false,
      selectedRealmId: '',
      characters: [],
      loadingCharacters: false,
      selectedCharacterId: '',
    }
  },
  watch: {
    selectedRealmId(newVal) {
      this.selectedCharacterId = ''
      this.characters = []
      if (newVal) this.loadCharactersForRealm(newVal)
    }
  },
  computed: {
    filteredUsers() {
      const term = (this.search || '').toLowerCase()
      const arr = this.users.filter(u => {
        return !term || (u.username || '').toLowerCase().includes(term) || (u.email || '').toLowerCase().includes(term)
      })
      return arr.sort((a,b) => (a.username||'').localeCompare(b.username||''))
    },
    characterItems() {
      return (this.characters || []).map(c => ({ text: c.name || 'Unnamed', value: c.id }))
    },
    selectedRealm() {
      return this.realms.find(r => r.id === this.selectedRealmId)
    },
    selectedCharacter() {
      return this.characters.find(c => c.id === this.selectedCharacterId)
    }
  },
  async created() {
    this.progressValue = 0
    this.storage = getStorage()
    await Promise.all([this.fetchUsers(), this.fetchRealms()])
    this.progressValue = 100
  },
  methods: {
    onRealmChange(val) {
      // Ensure selectedRealmId updates and characters load reliably
      this.selectedRealmId = val || ''
      this.selectedCharacterId = ''
      this.characters = []
      if (this.selectedRealmId) this.loadCharactersForRealm(this.selectedRealmId)
    },
    async reload() { await this.fetchUsers() },
    async fetchUsers() {
      this.loading = true
      this.errorMessage = ''
      try {
        const snap = await getDocs(collection(db, 'users'))
        this.users = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      } catch (e) {
        console.error('Failed to fetch users', e)
        this.errorMessage = e.message || 'Failed to load users'
      } finally {
        this.loading = false
      }
    },
    async fetchRealms() {
      this.loadingRealms = true
      this.realms = []
      try {
        const snapshot = await getDocs(collection(db, 'realms'))
        snapshot.docs.forEach(doc => {
          console.log(doc.id, doc.data())
          this.realms.push({
            id: doc.id,
            name: doc.data().name,
            characters: doc.data().characters
          })
        })
      } catch (e) {
        console.error('Failed to fetch realms', e)
      } finally {
        this.loadingRealms = false
      }
    },
    async loadCharactersForRealm(realmId) {
      if (!realmId) { this.characters = []; this.selectedCharacterId = ''; return }
      this.loadingCharacters = true
      try {
        const realm = this.realms.find(r => r.id === realmId) || null
        const embedded = Array.isArray(realm?.characters) ? realm.characters : []
        const list = embedded.map((c, i) => ({ id: `realm:${realmId}:${i}`, source: 'realm', name: c?.name || `Character ${i+1}`, avatar: c?.avatar?.image || (Array.isArray(c?.avatar?.images) ? c.avatar.images[0] : ''), realmId }))
        this.characters = list
      } catch (e) {
        console.error('Failed to use embedded realm characters', e)
        this.characters = []
      } finally {
        this.loadingCharacters = false
      }
    },
    openUser(user) {
      this.dialogError = ''
      this.editUser = { id: user.id, username: user.username || '', email: user.email || '', avatar: user.avatar || '', sparks: Number(user.sparks || 0) }
      this.dialog = true
      // Preselect realm and character from profile
      this.selectedRealmId = user?.realm?.id || ''
      this.selectedCharacterId = user?.characterId || ''
      if (this.selectedRealmId) {
        this.loadCharactersForRealm(this.selectedRealmId, this.selectedCharacterId)
      } else {
        this.characters = []
        this.selectedCharacterId = ''
      }
    },
    adjustSparks(delta) {
      const val = Number(this.editUser.sparks || 0) + Number(delta)
      this.editUser.sparks = isNaN(val) ? 0 : Math.max(0, val)
    },
    applySelectedCharacterToProfile() {
      if (!this.selectedCharacter) return
      this.editUser.username = this.selectedCharacter.name || this.editUser.username
      if (this.selectedCharacter.avatar) this.editUser.avatar = this.selectedCharacter.avatar
    },
    async onAvatarSelected(evt) {
      const file = evt.target.files && evt.target.files[0]
      if (!file) return
      this.uploadingAvatar = true
      try {
        const ext = (file.name.split('.').pop() || 'png').toLowerCase()
        const path = `public/users/${this.editUser.id}/avatar_${Date.now()}.${ext}`
        const ref = storageRef(this.storage, path)
        const result = await uploadBytes(ref, file)
        const url = await getDownloadURL(result.ref)
        this.editUser.avatar = url
      } catch (e) {
        console.error('Avatar upload failed', e)
        this.dialogError = e.message || 'Avatar upload failed'
      } finally {
        this.uploadingAvatar = false
      }
    },
    async save() {
      if (!this.editUser || !this.editUser.id) return
      this.saving = true
      this.dialogError = ''
      try {
        const userRef = doc(db, 'users', this.editUser.id)
        const batch = writeBatch(db)
        const realmObj = this.selectedRealm ? { id: this.selectedRealm.id, name: this.selectedRealm.name, landscape: this.selectedRealm.landscape?.image || '' } : null
        // Preserve existing characterId unless explicitly changed in the UI
        const currentUser = this.users.find(u => u.id === this.editUser.id) || {}
        const prevCharId = currentUser.characterId || null
        // Regenerate slug from username for uniqueness check in global characters
        const regeneratedSlug = (this.editUser.username || '')
          .toString()
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^\-+|\-+$/g, '')
        const requestedCharId = (typeof this.selectedCharacterId === 'string' && this.selectedCharacterId.length)
          ? this.selectedCharacterId
          : (prevCharId || regeneratedSlug || null)
        const newCharId = requestedCharId
        const newChar = this.characters.find(c => c.id === newCharId) || null
        

        // Release previous character if it was a global character and changed (and exists)
        if (prevCharId && prevCharId !== newCharId && typeof prevCharId === 'string' && !prevCharId.startsWith('realm:')) {
          const prevRef = doc(db, 'characters', prevCharId)
          const prevSnap = await getDoc(prevRef)
          if (prevSnap.exists()) {
            const prevData = prevSnap.data() || {}
            if (!prevData.userId || prevData.userId === this.editUser.id) {
              batch.update(prevRef, { status: 'available', userId: null, updatedAt: serverTimestamp() })
            }
          } else {
            console.warn('Previous character doc not found, skipping release:', prevCharId)
          }
        }
        // Claim new character if selected and is from global collection
        if (newCharId && newCharId !== prevCharId) {
          if (typeof newCharId === 'string' && !newCharId.startsWith('realm:')) {
            const newRef = doc(db, 'characters', newCharId)
            const newSnap = await getDoc(newRef)
            if (newSnap.exists()) {
              const newData = newSnap.data() || {}
              if (newData.userId && newData.userId !== this.editUser.id && newData.status === 'taken') {
                throw new Error('Character is already claimed by another user.')
              }
              // Enforce uniqueness by normalized slug
              const normalized = regeneratedSlug
              if (normalized && normalized !== newCharId) {
                const slugRef = doc(db, 'characters', normalized)
                const slugSnap = await getDoc(slugRef)
                if (slugSnap.exists() && slugSnap.id !== prevCharId) {
                  throw new Error('Username slug is already taken.')
                }
              }
              batch.update(newRef, { status: 'taken', userId: this.editUser.id, name: this.editUser.username || newCharId, updatedAt: serverTimestamp() })
            } else {
              // Create if missing, using regenerated slug for id to ensure uniqueness
              const finalId = regeneratedSlug || newCharId
              const finalRef = doc(db, 'characters', finalId)
              const finalSnap = await getDoc(finalRef)
              if (finalSnap.exists()) {
                const data = finalSnap.data() || {}
                if (data.userId && data.userId !== this.editUser.id && data.status === 'taken') {
                  throw new Error('Username slug is already taken.')
                }
                batch.update(finalRef, { status: 'taken', userId: this.editUser.id, name: this.editUser.username || finalId, updatedAt: serverTimestamp() })
                // Also switch the id we store on user to the slug id
                if (finalId !== newCharId) {
                  // override to slug id for user payload below
                  // will be assigned via userPayload
                }
              } else {
                // Create fresh doc
                batch.set(finalRef, {
                  id: finalId,
                  name: this.editUser.username || finalId,
                  status: 'taken',
                  userId: this.editUser.id,
                  avatar: this.editUser.avatar || '',
                  realmId: this.selectedRealmId || null,
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp()
                })
              }
            }
          } // else embedded character – nothing to do in global collection
        }

        const userPayload = {
          username: this.editUser.username || '',
          avatar: this.editUser.avatar || '',
          sparks: Number(this.editUser.sparks || 0),
          characterId: regeneratedSlug || newCharId || null,
          realm: realmObj,
          updatedAt: serverTimestamp(),
        }
        batch.update(userRef, userPayload)
        await batch.commit()
        // Reflect in list
        const idx = this.users.findIndex(u => u.id === this.editUser.id)
        if (idx !== -1) this.$set(this.users, idx, { ...this.users[idx], ...userPayload })
        this.dialog = false
        if (this.$snackbar && this.$snackbar.success) this.$snackbar.success('User updated')
      } catch (e) {
        console.error('Failed to save user', e)
        this.dialogError = e.message || 'Failed to save user'
      } finally {
        this.saving = false
      }
    }
  },
  
}
</script>